// ============================================================================
// CONFIG SETTINGS HOOK
// ============================================================================
// Central state management for the Configuration Settings screen.
// Ported from crs-web/src/hooks/api/useConfigSettings.ts and adapted to
// PRIMA's scope hierarchy: SYSTEM → OFFICE → EMPLOYEE.

import { useState, useCallback, useEffect, useRef } from 'react';
import { configService } from 'api/services/configService';
import notificationService from 'services/notificationService';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
  ConfigScopeRef,
} from 'api/types/config.types';

export type ScopeType = 'SYSTEM' | 'OFFICE' | 'EMPLOYEE';

export interface ConfigSettingsState {
  groups: ConfigGroup[];
  groupsLoading: boolean;
  activeGroupKey: string | null;
  scopeType: ScopeType;
  selectedOfficeId: string | null;
  selectedEmployeeId: string | null;
  effectiveValues: ConfigEffectiveValue[];
  definitions: ConfigDefinition[];
  editedValues: Record<string, ConfigValueUpdate>;
  hasChanges: boolean;
  loading: boolean;
  saving: boolean;
  error: Error | null;
}

export const useConfigSettings = () => {
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeType>('SYSTEM');
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [effectiveValues, setEffectiveValues] = useState<ConfigEffectiveValue[]>([]);
  const [definitions, setDefinitions] = useState<ConfigDefinition[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, ConfigValueUpdate>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hasChanges = Object.keys(editedValues).length > 0;

  // Track whether first group was auto-selected
  const autoSelectedRef = useRef(false);

  // Fetch all groups on mount
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const response = await configService.getGroups();
      const sorted = [...(response.data ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      setGroups(sorted);
      // Auto-select first group
      if (sorted.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        setActiveGroupKey(sorted[0].groupKey);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Build scopes array from current scope type + selections
  const buildScopes = useCallback(
    (
      scope: ScopeType,
      officeId: string | null,
      employeeId: string | null
    ): ConfigScopeRef[] => {
      const scopes: ConfigScopeRef[] = [];
      if ((scope === 'OFFICE' || scope === 'EMPLOYEE') && officeId) {
        scopes.push({ type: 'OFFICE', id: officeId });
      }
      if (scope === 'EMPLOYEE' && employeeId) {
        scopes.push({ type: 'EMPLOYEE', id: employeeId });
      }
      return scopes;
    },
    []
  );

  // Fetch definitions + effective values when group or scope changes
  const fetchGroupData = useCallback(
    async (
      groupKey: string,
      scope: ScopeType,
      officeId: string | null,
      employeeId: string | null
    ) => {
      setLoading(true);
      setError(null);
      try {
        const scopes = buildScopes(scope, officeId, employeeId);

        const [defsResponse, effectiveResponse] = await Promise.all([
          configService.getDefinitions(groupKey),
          configService.getEffectiveConfig(groupKey, scopes),
        ]);

        setDefinitions(defsResponse.data ?? []);
        setEffectiveValues(effectiveResponse.data ?? []);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [buildScopes]
  );

  // Re-fetch when group, scope, office, or employee changes
  useEffect(() => {
    if (!activeGroupKey) return;
    if (scopeType === 'OFFICE' && !selectedOfficeId) {
      setEffectiveValues([]);
      setDefinitions([]);
      return;
    }
    if (scopeType === 'EMPLOYEE' && !selectedEmployeeId) {
      if (!selectedEmployeeId) {
        setEffectiveValues([]);
        setDefinitions([]);
      }
      return;
    }
    setEditedValues({});
    fetchGroupData(activeGroupKey, scopeType, selectedOfficeId, selectedEmployeeId);
  }, [activeGroupKey, scopeType, selectedOfficeId, selectedEmployeeId, fetchGroupData]);

  // Auto-downgrade scope when switching to a group that doesn't support EMPLOYEE
  useEffect(() => {
    if (scopeType === 'EMPLOYEE' && definitions.length > 0) {
      const hasEmployeeLevel = definitions.some((d) => d.maxScopeLevel >= 2);
      if (!hasEmployeeLevel) {
        setScopeType('OFFICE');
        setSelectedEmployeeId(null);
      }
    }
  }, [definitions, scopeType]);

  // Update a single value
  const updateValue = useCallback(
    (configKey: string, value?: string, listValues?: ConfigListItem[]) => {
      setEditedValues((prev) => ({
        ...prev,
        [configKey]: { configKey, value, listValues },
      }));
    },
    []
  );

  // Reset a value (signals override deletion at non-SYSTEM scope)
  const resetValue = useCallback((configKey: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [configKey]: { configKey, value: undefined, listValues: undefined },
    }));
  }, []);

  // Discard all changes
  const discardChanges = useCallback(() => {
    setEditedValues({});
  }, []);

  // Determine the scopeId for save — the most specific scope selected
  const getScopeId = useCallback((): string | undefined => {
    if (scopeType === 'EMPLOYEE' && selectedEmployeeId) return selectedEmployeeId;
    if (scopeType === 'OFFICE' && selectedOfficeId) return selectedOfficeId;
    return undefined;
  }, [scopeType, selectedOfficeId, selectedEmployeeId]);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (!activeGroupKey || !hasChanges) return;

    setSaving(true);
    try {
      const values = Object.values(editedValues);
      const requestBody = {
        scopeType,
        scopeId: getScopeId() ?? null,
        values,
      };
      // Debug: dump request to console so we can see exactly what's sent
      // (API returns the same envelope on validation failures)
      // eslint-disable-next-line no-console
      console.log('[ConfigSave] PUT /api/v1/Config/values request:', requestBody);
      await configService.updateValues(requestBody);

      // Re-fetch effective values after save
      await fetchGroupData(activeGroupKey, scopeType, selectedOfficeId, selectedEmployeeId);
      setEditedValues({});
      notificationService.toast.success('Configuration settings saved successfully.');
    } catch (err: any) {
      // Extract the API's actual error message from the response envelope.
      // Backend returns { success: false, message, errors: [...] }.
      const resp = err?.response?.data;
      const apiMessage =
        resp?.message ||
        (Array.isArray(resp?.errors) && resp.errors.length > 0
          ? resp.errors.join('; ')
          : null);
      const fallback = err?.message || 'Failed to save configuration settings.';
      const finalMessage = apiMessage || fallback;
      // eslint-disable-next-line no-console
      console.error('[ConfigSave] save failed', { status: err?.response?.status, body: resp, error: err });
      setError(err as Error);
      notificationService.toast.error(finalMessage, 8000);
    } finally {
      setSaving(false);
    }
  }, [
    activeGroupKey,
    hasChanges,
    editedValues,
    scopeType,
    selectedOfficeId,
    selectedEmployeeId,
    getScopeId,
    fetchGroupData,
  ]);

  // Select a group
  const selectGroup = useCallback((groupKey: string) => {
    setActiveGroupKey(groupKey);
  }, []);

  // Change scope type
  const changeScopeType = useCallback((newScope: ScopeType) => {
    setScopeType(newScope);
    if (newScope === 'SYSTEM') {
      setSelectedOfficeId(null);
      setSelectedEmployeeId(null);
    }
    if (newScope === 'OFFICE') {
      setSelectedEmployeeId(null);
    }
  }, []);

  // Select an office
  const selectOffice = useCallback((officeId: string | null) => {
    setSelectedOfficeId(officeId);
    // Clear employee when office changes
    setSelectedEmployeeId(null);
  }, []);

  // Select an employee
  const selectEmployee = useCallback((employeeId: string | null) => {
    setSelectedEmployeeId(employeeId);
  }, []);

  return {
    // State
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedOfficeId,
    selectedEmployeeId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    error,
    // Actions
    selectGroup,
    changeScopeType,
    selectOffice,
    selectEmployee,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  };
};
